/**
 * API Service for Assessment Builder
 * Centralizes all backend API calls for layout templates and navigation rules
 */

import api from './api';

const API_BASE = '/api/assessment-builder';

// ============================================================================
// LAYOUT TEMPLATE APIs
// ============================================================================

/**
 * Check if a layout default exists for a question type in an assessment
 * Used to determine if "first question modal" should be shown
 */
export const checkLayoutDefault = async (assessmentId, questionType) => {
  const response = await api.post(`${API_BASE}/check-layout-default/`, {
    assessment_id: assessmentId,
    question_type: questionType
  });
  return response.data;
};

/**
 * Get available layout templates for a question type
 * Returns system, sponsor, and other assessment templates
 */
export const getAvailableTemplates = async (questionType, assessmentId) => {
  const params = new URLSearchParams({
    question_type: questionType,
    ...(assessmentId && { assessment_id: assessmentId })
  });
  
  const response = await api.get(`${API_BASE}/layout-templates/available/?${params}`);
  return response.data;
};

/**
 * Save a new question layout template
 * Creates a new template with the provided layout configuration
 */
export const saveQuestionLayoutTemplate = async (templateData) => {
  try {
    const response = await api.post(`${API_BASE}/layout-templates/`, templateData);
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Failed to save layout template';
    throw new Error(errorMsg);
  }
};

/**
 * Set a layout template as default for a question type in an assessment
 * Can either use existing template or create new from layout_config
 */
export const setLayoutDefault = async (data) => {
  const response = await api.post(`${API_BASE}/layout-templates/set-default/`, data);
  return response.data;
};

/**
 * Set form-wide formatting (font family, base font size, line spacing) for an
 * assessment. Pass { reset: true } to revert to publisher defaults. (FECOA-732)
 */
export const setGlobalFormatting = async (assessmentId, formatting) => {
  const response = await api.patch(
    `${API_BASE}/assessments/${assessmentId}/global-formatting/`,
    formatting
  );
  return response.data;
};

/**
 * Copy all layout defaults from one assessment to another
 */
export const copyLayoutsFromAssessment = async (sourceAssessmentId, targetAssessmentId) => {
  const response = await api.post(`${API_BASE}/copy-layouts-from-assessment/`, {
    source_assessment_id: sourceAssessmentId,
    target_assessment_id: targetAssessmentId
  });
  return response.data;
};

// ============================================================================
// AUTO-BUILD SCREENS API
// ============================================================================

/**
 * Auto-build screens from imported items with optional template configuration.
 * Creates SurveyScreen + ScreenQuestion records on the backend.
 */
export const autoBuildScreens = async (assessmentId, { item_ids, template_config, device_mode }) => {
  const response = await api.post(
    `${API_BASE}/assessments/${assessmentId}/auto-build-screens/`,
    { item_ids, template_config, device_mode }
  );
  return response.data;
};

/**
 * Clone items from VPG/Sponsor/Study tiers into the assessment library for
 * the given assessment. Returns the assessment-scoped items so callers can
 * add them to the sidebar / pass them into auto-build with the new IDs.
 */
export const importItemsToAssessment = async (assessmentId, itemIds) => {
  const response = await api.post(
    `${API_BASE}/item-bank/import-to-assessment/`,
    { assessment_id: assessmentId, item_ids: itemIds }
  );
  return response.data;
};

// ============================================================================
// NAVIGATION RULES APIs
// ============================================================================

/**
 * Get navigation rules for a screen question
 */
export const getNavigationRules = async (screenQuestionId) => {
  const response = await api.get(`${API_BASE}/screen-questions/${screenQuestionId}/navigation-rules/`);
  return response.data;
};

/**
 * Create or update navigation rules for a screen question
 */
export const saveNavigationRules = async (screenQuestionId, rulesData) => {
  const response = await api.post(`${API_BASE}/screen-questions/${screenQuestionId}/navigation-rules/`, rulesData);
  return response.data;
};

/**
 * Update existing navigation rules
 */
export const updateNavigationRules = async (screenQuestionId, rulesData) => {
  const response = await api.put(`${API_BASE}/screen-questions/${screenQuestionId}/navigation-rules/`, rulesData);
  return response.data;
};

/**
 * Delete navigation rules for a screen question
 */
export const deleteNavigationRules = async (screenQuestionId) => {
  const response = await api.delete(`${API_BASE}/screen-questions/${screenQuestionId}/navigation-rules/`);
  return response.data;
};

/**
 * Evaluate navigation rules given participant responses
 * Used during survey taking to determine next screen
 */
export const evaluateNavigation = async (screenQuestionId, participantResponses) => {
  const response = await api.post(`${API_BASE}/evaluate-navigation/`, {
    screen_question_id: screenQuestionId,
    participant_responses: participantResponses
  });
  return response.data;
};

/**
 * Get available navigation rule templates
 * Returns system and sponsor templates
 */
export const getNavigationTemplates = async () => {
  const response = await api.get(`${API_BASE}/navigation-templates/`);
  return response.data;
};

/**
 * Apply a navigation rule template to a screen question
 */
export const applyNavigationTemplate = async (screenQuestionId, templateId, parameterMapping = {}) => {
  const response = await api.post(`${API_BASE}/screen-questions/${screenQuestionId}/apply-template/`, {
    screen_question_id: screenQuestionId,
    template_id: templateId,
    parameter_mapping: parameterMapping
  });
  return response.data;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert local skip rules format to backend API format
 */
export const convertSkipRulesToApiFormat = (rules) => {
  return rules.map(rule => ({
    id: rule.id,
    condition_type: rule.condition_type,
    operator: rule.operator,
    value: rule.value,
    question_ids: rule.question_ids || [],
    backend_question_ids: rule.backend_question_ids || [],  // Actual DB question IDs for evaluation
    // answer_combination specific fields
    conditions: rule.conditions || [],  // Frontend element ID-based conditions
    backend_conditions: rule.backend_conditions || [],  // DB question ID-based conditions for backend evaluation
    logic: rule.logic || 'and',  // AND/OR logic for answer_combination
    action_type: rule.action_type,
    target_screen_id: rule.target_screen_id || null,
    target_screen_name: rule.target_screen_name || null,
    target_screen_index: rule.target_screen_index,
    alert_message: rule.alert_message || '',
    enabled: rule.enabled !== false
  }));
};

/**
 * Convert API rules format to local display format
 */
export const convertApiRulesToLocalFormat = (apiRules) => {
  if (!apiRules || !apiRules.skip_rules) return [];
  
  return apiRules.skip_rules.map((rule, index) => ({
    id: rule.id || `temp_${index}`,
    condition_type: rule.condition_type,
    operator: rule.operator,
    value: rule.value,
    question_ids: rule.question_ids || [],
    backend_question_ids: rule.backend_question_ids || [],  // Preserve DB question IDs
    // answer_combination specific fields
    conditions: rule.conditions || [],  // Frontend element ID-based conditions
    backend_conditions: rule.backend_conditions || [],  // DB question ID-based conditions
    logic: rule.logic || 'and',  // AND/OR logic for answer_combination
    action_type: rule.action_type,
    target_screen_id: rule.target_screen_id,
    target_screen_name: rule.target_screen_name,
    target_screen_index: rule.target_screen_index,
    alert_message: rule.alert_message || '',
    enabled: rule.enabled !== false
  }));
};

// ============================================================================
// SCREEN MANAGEMENT APIs
// ============================================================================

/**
 * Save screens for an assessment
 * Uses axios api instance with automatic token refresh on 401 errors
 * @param {number} assessmentId - Assessment ID
 * @param {number} surveyId - Survey ID
 * @param {Array} screens - Screen data to save
 * @param {Array} items - Sidebar items to persist (optional)
 * @param {Object} options - Additional options (device_mode, target_device_id)
 */
export const saveScreens = async (assessmentId, surveyId, screens, items = [], options = {}) => {
  // Debug: Log items with IRT params
  const itemsWithIrt = items.filter(i => i.irt_parameters && Object.keys(i.irt_parameters).length > 0);
  console.debug('[SAVE_SCREENS] Total items:', items.length, 'Items with IRT params:', itemsWithIrt.length);
  if (itemsWithIrt.length > 0) {
    console.debug('[SAVE_SCREENS] IRT items:', itemsWithIrt.map(i => ({ id: i.id, code: i.code, irt: i.irt_parameters })));
  }
  
  const response = await api.post(`${API_BASE}/assessments/${assessmentId}/save_screens/`, {
    survey_id: surveyId,
    screens: screens,
    items: items,
    device_mode: options.device_mode || null,
    target_device_id: options.target_device_id || null
  });
  
  return response.data;
};

// ============================================================================
// EXCLUSIVE EDIT LOCK APIs (FECOA-1302)
// ============================================================================

/**
 * Acquire or refresh the exclusive edit lock for a draft assessment.
 * Called on editor mount and on a recurring heartbeat. Resolves with the lock
 * payload when held by the caller. A 423 response (another active editor) is
 * thrown by axios and should be caught by the caller to enter read-only mode.
 * @param {string} assessmentUuid - Assessment UUID
 */
export const acquireLock = async (assessmentUuid) => {
  const response = await api.post(`${API_BASE}/assessments/${assessmentUuid}/acquire-lock/`);
  return response.data;
};

/**
 * Release the edit lock if the caller holds it. Safe to call on unmount.
 * @param {string} assessmentUuid - Assessment UUID
 */
export const releaseLock = async (assessmentUuid) => {
  const response = await api.post(`${API_BASE}/assessments/${assessmentUuid}/release-lock/`);
  return response.data;
};

/**
 * Read the current lock state without acquiring it. Used by read-only viewers
 * to poll for the holder releasing the assessment.
 * @param {string} assessmentUuid - Assessment UUID
 */
export const getLockStatus = async (assessmentUuid) => {
  const response = await api.get(`${API_BASE}/assessments/${assessmentUuid}/lock-status/`);
  return response.data;
};

// ============================================================================
// ARCHIVE / SOFT DELETE APIs
// ============================================================================

/**
 * Get archived (soft-deleted) assessments
 * @param {Object} params - Query params (search, library_type)
 */
export const getArchivedAssessments = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.library_type) queryParams.append('library_type', params.library_type);
  
  const response = await api.get(`${API_BASE}/assessments/archived/?${queryParams}`);
  return response.data;
};

/**
 * Restore a soft-deleted assessment
 * @param {number} assessmentId - Assessment ID to restore
 */
export const restoreAssessment = async (assessmentId) => {
  const response = await api.post(`${API_BASE}/assessments/${assessmentId}/restore/`);
  return response.data;
};

/**
 * Permanently delete a soft-deleted assessment (superuser only)
 * @param {number} assessmentId - Assessment ID to permanently delete
 */
export const permanentDeleteAssessment = async (assessmentId) => {
  const response = await api.post(`${API_BASE}/assessments/${assessmentId}/permanent_delete/`);
  return response.data;
};
